import {
    autoPlacement,
    autoUpdate,
    safePolygon,
    useDismiss,
    useFloating,
    useHover,
    useInteractions,
} from '@floating-ui/react';
import { useState } from 'react';
import type { AnnotatedTextVisualization, TooltipSection } from '../../types';
import { renderMarkdownLinks } from '../../utils';

interface AnnotatedTextRendererProps {
    data: AnnotatedTextVisualization;
    className?: string;
}

function CardTitle({ title, url }: { title: string; url?: string }) {
    if (url) {
        return (
            <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="weavable-annotated-text__tooltip-card-title-link"
            >
                {title}
            </a>
        );
    }
    return <span className="weavable-annotated-text__tooltip-card-title">{title}</span>;
}

function PopoverContent({ sections }: { sections: TooltipSection[] }) {
    return (
        <div className="weavable-annotated-text__tooltip-popover">
            {sections.map((section, si) => (
                <div key={si} className="weavable-annotated-text__tooltip-section">
                    <div className="weavable-annotated-text__tooltip-section-title">{section.title}</div>
                    <div className="weavable-annotated-text__tooltip-cards">
                        {section.cards.map((card, ci) => (
                            <div key={ci} className="weavable-annotated-text__tooltip-card">
                                <CardTitle title={card.title} url={card.url} />
                                {card.details.map((detail, di) => (
                                    <span key={di} className="weavable-annotated-text__tooltip-detail">
                                        {detail.label}: {detail.value}
                                    </span>
                                ))}
                            </div>
                        ))}
                    </div>
                </div>
            ))}
        </div>
    );
}

function InfoTooltip({ sections }: { sections: TooltipSection[] }) {
    const [open, setOpen] = useState(false);

    const { refs, floatingStyles, context } = useFloating({
        whileElementsMounted: autoUpdate,
        middleware: [autoPlacement({ allowedPlacements: ['bottom-start', 'bottom-end', 'top-start', 'top-end'] })],
        open,
        onOpenChange: setOpen,
    });

    const hover = useHover(context, { handleClose: safePolygon() });
    const dismiss = useDismiss(context);
    const { getReferenceProps, getFloatingProps } = useInteractions([hover, dismiss]);

    return (
        <>
            <span ref={refs.setReference} {...getReferenceProps()} className="weavable-annotated-text__tooltip-trigger">
                <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="weavable-annotated-text__info-icon"
                    style={{
                        color: open
                            ? 'var(--weavable-primary-color, #0066cc)'
                            : 'var(--weavable-text-secondary, #6b7280)',
                    }}
                >
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" y1="16" x2="12" y2="12" />
                    <line x1="12" y1="8" x2="12.01" y2="8" />
                </svg>
            </span>
            {open && (
                <div
                    ref={refs.setFloating}
                    style={{ ...floatingStyles, marginTop: '6px' }}
                    className="weavable-annotated-text__floating"
                    {...getFloatingProps()}
                >
                    <PopoverContent sections={sections} />
                </div>
            )}
        </>
    );
}

/**
 * Renders an AnnotatedText visualization with markdown links,
 * optional subtitle, and optional info-icon tooltip.
 */
export function AnnotatedTextRenderer({ data, className }: AnnotatedTextRendererProps) {
    const { tooltip } = data;

    return (
        <div className={`weavable-annotated-text ${className || ''}`.trim()}>
            {tooltip && tooltip.length > 0 && <InfoTooltip sections={tooltip} />}
            <div className="weavable-annotated-text__content">
                <span className="weavable-annotated-text__markdown">
                    {renderMarkdownLinks(data.markdown, 'weavable-annotated-text__link')}
                </span>
                {data.subtitle && <span className="weavable-annotated-text__subtitle">{data.subtitle}</span>}
            </div>
        </div>
    );
}
